/**
 * Scheduling an event at a place (place manager, unit 6b): one form (game ·
 * when · rules · title, a template optional), a one-line review, then the
 * create, with an undo for five minutes. The only write the places pages
 * make, and only for pop.in staff on a dev or preview build
 * ({@link canScheduleEvents}); the admin console stays the full editor.
 *
 * Everything here is pure, so the defaults, the input sent and the review
 * line are unit-testable; the calls live in `schedule-event-api.ts`, injected
 * through {@link ScheduleOptionsApi} and {@link ScheduleWriteApi}.
 *
 * The event is made at THIS place (`placeId`, never picked). It reaches Next
 * up only through one of the place's seasons (no `Place.events` exists; see
 * `place-events-api.ts`), so the form picks a season too: the one running at
 * the start, by default.
 *
 * Defaults, and where each comes from (the PR lists them too):
 *   - the input's other required fields: the admin console's new event
 *     (`admin-gui/src/modules/models/EventModel.ts`, `createNewEventModel`),
 *     {@link ADMIN_EVENT_DEFAULTS};
 *   - the game: the game the place pins for its main room, when it pins one
 *     and it is enabled; the rules: the preset the main room runs, for the
 *     lobby and the tables both, when it is a preset (else the admin
 *     console's two); the start: the next round hour at least ten minutes
 *     out; the run: 60 minutes;
 *     the title: "<Game> at <Place>"; the host: the signed-in user;
 *   - a template: everything in its JSON (as the admin console's "new event
 *     from template" takes it, `admin-gui/src/pages/events/new.tsx`), its own
 *     run length, and its times recomputed: the start from the form, reveal
 *     now, wind-down after the run.
 */
import { windowLabel } from "@/lib/dates";
import { undoOffered, undoWindowOpen } from "@/features/writes/writes";
import { GRAPHQL_ENDPOINT, SCHEDULING_ENABLED } from "@/config";
import type { PlaceSeasonsData } from "@/data/place-events-api";
import type { GameCatalogData } from "@/data/place-games-api";
import type { CreateEventInput, EventToChangeData, MadeEventData, UpdateEventInput } from "@/data/schedule-event-api";
import type { PresetChoice } from "@/data/presets-api";
import type { EventsViewer } from "@/features/places/last-events";
import type { PlaceGames } from "@/features/places/games";
import { templateKeysOf, templateObjectOf, type TemplateRow } from "@/features/places/schedule";

const MINUTE = 60 * 1000;

/** The backends scheduling may write to: dev-api, and a backend on this machine. */
const WRITABLE_HOSTS = new Set(["dev-api.pop.in", "localhost", "127.0.0.1"]);

/** The dev/preview gate, both halves needed: its own flag,
 *  `BUN_PUBLIC_ENABLE_SCHEDULING` "true" (the dev and preview builds,
 *  `.env.development`; off on prod, `.env.production`, and wherever absent),
 *  and an endpoint on dev-api or this machine (so no build can ever write to
 *  another backend from here, whatever its flags say). */
export function schedulingAllowed(flagOn: boolean, endpoint: string | undefined): boolean {
  if (!flagOn || !endpoint) return false;
  try {
    return WRITABLE_HOSTS.has(new URL(endpoint).hostname);
  } catch {
    return false;
  }
}

/** {@link schedulingAllowed} for this build. Prod shows no way to schedule. */
export const SCHEDULING_ALLOWED = schedulingAllowed(SCHEDULING_ENABLED, GRAPHQL_ENDPOINT);

/** Whether this viewer gets "Schedule an event": pop.in staff (the create and
 *  its undo are admin-only, through the staff door), on a build where the
 *  gate is on. Never a brand's staff; never on prod. */
export function canScheduleEvents(viewer: EventsViewer, allowed: boolean = SCHEDULING_ALLOWED): boolean {
  return viewer.isSuperAdmin && allowed;
}

/** The admin console's values for the input's required fields the form does
 *  not ask about (`createNewEventModel`): public, one table to start, final
 *  results shown for an hour, no countdown, no matchmaking, tables shown as
 *  cards, deep links on. (No Intercom segment: `buildCreateEventInput` fixes it.) */
export const ADMIN_EVENT_DEFAULTS = {
  visibility: "PUBLIC",
  description: "",
  gatheringCount: 1,
  showFinalResultsDuration: 60 * MINUTE,
  hasCountdownState: false,
  matchmakingEnabled: false,
  subgatheringViewMode: "card",
  deepLinkToSubgatheringsEnabled: true,
  leaderboardTitle: "Leaderboard",
} as const satisfies Partial<CreateEventInput>;

/** The admin console's rules presets for a new event, by slug: the lobby's
 *  and the tables'. Used when the main room's preset is not one. */
export const ADMIN_PRESETS = { lobby: "event-lobby", tables: "event-multi-room" } as const;

/** Who hosts when the signed-in user has no username (the admin console's own fallback). */
export const FALLBACK_HOST = "popinbot";

/** How long an event runs by default. */
export const DEFAULT_RUN_MINUTES = 60;

/** How soon an event may start: two minutes out (the admin console's new
 *  event starts then). Sooner, an undo would end it rather than cancel it. */
export const SOONEST_START_MS = 2 * MINUTE;

/** How far out the default start is at least: the next round hour past this,
 *  so a form left open a few minutes still passes {@link SOONEST_START_MS}. */
export const DEFAULT_LEAD_MS = 10 * MINUTE;

/** Within this long of its start, the backend's `cancelEvent` ends an event
 *  (starts and finishes it) instead of cancelling it. */
export const END_INSTEAD_MS = 60 * 1000;

/** How far this browser's clock may be from the backend's, for saying which of
 *  the two an undo did (the backend decides by its own clock). */
const CLOCK_SKEW_MS = 30 * 1000;

/** Every field of `AdminCreateEventInput` (checked against the schema below:
 *  a field added or gone fails the typecheck). A template's JSON is a saved
 *  admin form, so it may hold keys the input no longer takes (`fairPrizings`
 *  once); only these are sent. */
const CREATE_EVENT_FIELDS = [
  "attachedProductIds", "beginWindDownAt", "countdownTime", "deepLinkToSubgatheringsEnabled", "description",
  "eventTemplateId", "featured", "flatPrizingConfigurationId", "gatheringActivitySlug", "gatheringCount",
  "gatheringGameModeId", "gatheringHostUsername", "gatheringSettingsTagSlug", "guestListByUsernames",
  "hasCountdownState", "howItWorksIntercomArticleId", "intercomNotificationSegment", "isTemplate",
  "leaderboardScoringMetrics", "leaderboardTitle", "lobbyGatheringSettingsTagSlug", "matchmakingEnabled",
  "matchmakingWaitingText", "overrideMatchmakingAbsoluteMaxWaitTimeMs", "overrideMatchmakingMaxPlayerCount",
  "overrideMatchmakingMinPlayerCount", "overrideMatchmakingPlayerCounts",
  "overrideMatchmakingWaitTimeForPartialMatchMs", "placeId", "revealAt", "seasonId", "showFinalResultsDuration",
  "startAt", "subgatheringViewMode", "templateDescription", "templateName", "tileBackground",
  "tileBackgroundCloudPath", "title", "visibility",
] as const satisfies readonly (keyof CreateEventInput)[];

type Listed = (typeof CREATE_EVENT_FIELDS)[number];
/** True only while every input field is listed above. */
const EVERY_FIELD_LISTED: Exclude<keyof CreateEventInput, Listed> extends never ? true : never = true;
void EVERY_FIELD_LISTED;

/** Keys of a template's JSON never sent as they are: its times (recomputed),
 *  where and by whom (this place, this user), and what made it a template. */
const NOT_FROM_TEMPLATE: ReadonlySet<Listed> = new Set<Listed>([
  "startAt", "revealAt", "beginWindDownAt", "placeId", "isTemplate", "gatheringHostUsername",
  "eventTemplateId", "templateName", "templateDescription",
]);

/** A game the form offers: an enabled catalog game. */
export interface GameOption {
  slug: string;
  name: string;
}

/** A rules preset the form offers. */
export interface PresetOption {
  slug: string;
  name: string;
}

/** A season of this place the form offers: one not ended. */
export interface SeasonOption {
  id: string;
  title: string;
  startAt: number;
  endAt: number;
}

/** What the form picks from, read when it opens (staff only). */
export interface ScheduleOptions {
  games: GameOption[];
  presets: PresetOption[];
}

/** The two reads the form makes (the presets page's presets read,
 *  `presets-api.ts`, and the unit 5 catalog read, with the viewer bound). */
export interface ScheduleOptionsApi {
  catalog(): Promise<GameCatalogData>;
  presets(): Promise<PresetChoice[]>;
}

/** The games and presets from what was read: the catalog's enabled games,
 *  in its order; every preset with a slug (an event names its presets by
 *  slug). Archived presets are not among them (the read leaves them out). */
export function scheduleOptionsOf(catalog: GameCatalogData, presets: readonly PresetChoice[]): ScheduleOptions {
  return {
    games: catalog.nodes.filter((g) => g.isEnabled).map((g) => ({ slug: g.slug, name: g.name })),
    presets: presets.flatMap((p) => (p.slug ? [{ slug: p.slug, name: p.name }] : [])),
  };
}

/** Read the games and presets, side by side (see {@link scheduleOptionsOf}). */
export async function readScheduleOptions(api: ScheduleOptionsApi): Promise<ScheduleOptions> {
  const [catalog, presets] = await Promise.all([api.catalog(), api.presets()]);
  return scheduleOptionsOf(catalog, presets);
}

/** The place's seasons an event can join: those not ended, soonest start first. */
export function seasonOptionsOf(seasons: PlaceSeasonsData | null, now: number): SeasonOption[] {
  return (seasons?.seasons.nodes ?? [])
    .filter((s) => s.endAt > now)
    .sort((a, b) => a.startAt - b.startAt)
    .map((s) => ({ id: s.id, title: s.title, startAt: s.startAt, endAt: s.endAt }));
}

/** The season an event starting at `at` joins by default: the one running
 *  then (the latest to begin, when several are); null when none is. */
export function seasonAt(seasons: readonly SeasonOption[], at: number): string | null {
  const running = seasons.filter((s) => s.startAt <= at && at < s.endAt);
  return running.sort((a, b) => b.startAt - a.startAt)[0]?.id ?? null;
}

/** The next round hour after `now`, in the reader's own time zone: 2:20 PM
 *  gives 3:00 PM, and so does 2:00 PM exactly. */
export function nextRoundHour(now: number): number {
  const d = new Date(now);
  d.setMinutes(60, 0, 0);
  return d.getTime();
}

/** The default title: "Hearts at The Parlor". */
export function defaultTitle(game: string, place: string): string {
  return `${game} at ${place}`;
}

/** The form as filled in. Slugs for the game and presets ("" = not picked). */
export interface ScheduleDraft {
  game: string;
  startAt: number;
  minutes: number;
  seasonId: string | null;
  lobbyPreset: string;
  tablesPreset: string;
  title: string;
  /** The template it starts from (a template of this place), by id; null: none. */
  templateId: string | null;
}

/** What the defaults are worked out from. */
export interface DraftContext {
  now: number;
  placeName: string;
  /** The game the place pins for its main room (`defaultActivityForRootGathering`), by slug; null when it pins none. */
  mainGame: string | null;
  /** The rules preset the main room runs, by slug; null when it runs none. */
  mainPreset: string | null;
  options: ScheduleOptions;
  seasons: readonly SeasonOption[];
}

const offered = (list: readonly { slug: string }[], slug: string | null): slug is string =>
  slug !== null && list.some((o) => o.slug === slug);

/** The name a slug stands for in a list (the slug itself when not listed). */
export function nameOf(list: readonly { slug: string; name: string }[], slug: string): string {
  return list.find((o) => o.slug === slug)?.name ?? slug;
}

/** The defaults' context from the page's reads: the main room's pinned game
 *  (`PlaceGames.startsOn.main`, when the place pins one; null while the games
 *  have not read) and its rules preset (the room-now read), and the place's
 *  seasons not ended (null while they have not read: then no season). */
export function draftContextOf({
  now,
  place,
  games,
  seasons,
  options,
}: {
  now: number;
  /** The place: its name and its main room's rules preset (`PlaceData` fits). */
  place: { name: string; rootGathering: { settings: { slug: string | null } | null } | null };
  games: PlaceGames | null;
  seasons: PlaceSeasonsData | null;
  options: ScheduleOptions;
}): DraftContext {
  const main = games?.startsOn.main;
  return {
    now,
    placeName: place.name,
    mainGame: main?.pinned ? (main.game?.slug ?? null) : null,
    mainPreset: place.rootGathering?.settings?.slug ?? null,
    options,
    seasons: seasonOptionsOf(seasons, now),
  };
}

/** The form's first state (see the module comment for each default). */
export function draftDefaults(ctx: DraftContext): ScheduleDraft {
  const { games, presets } = ctx.options;
  const game = offered(games, ctx.mainGame) ? ctx.mainGame : "";
  // The next round hour at least ten minutes out (always a start allowed).
  const startAt = nextRoundHour(ctx.now + DEFAULT_LEAD_MS);
  const main = offered(presets, ctx.mainPreset) ? ctx.mainPreset : null;
  return {
    game,
    startAt,
    minutes: DEFAULT_RUN_MINUTES,
    seasonId: seasonAt(ctx.seasons, startAt),
    lobbyPreset: main ?? (offered(presets, ADMIN_PRESETS.lobby) ? ADMIN_PRESETS.lobby : ""),
    tablesPreset: main ?? (offered(presets, ADMIN_PRESETS.tables) ? ADMIN_PRESETS.tables : ""),
    title: game ? defaultTitle(nameOf(games, game), ctx.placeName) : "",
    templateId: null,
  };
}

/** The fields a template can set on the draft. */
export type TemplateField = "game" | "lobbyPreset" | "tablesPreset" | "seasonId" | "minutes" | "title";

/** The draft after switching template (or to none), and the fields the new
 *  template set. Fields the previous template set (`previous`; the form drops
 *  a field from it once the user changes that field) start over from the
 *  defaults, so nothing of a template backed out of stays; what the user set
 *  stays. Then the template's game, rules and season where its JSON names ones
 *  the form offers, its title where it names one (unless the user typed one,
 *  `titleTyped`), and its run length. A title neither set follows the game. */
export function withTemplate(
  draft: ScheduleDraft,
  previous: readonly TemplateField[],
  template: TemplateRow | null,
  ctx: DraftContext,
  titleTyped: boolean,
): { draft: ScheduleDraft; set: TemplateField[] } {
  const fresh = draftDefaults(ctx);
  const next: ScheduleDraft = { ...draft, templateId: template?.id ?? null };
  for (const f of previous) {
    if (f === "seasonId") next.seasonId = seasonAt(ctx.seasons, draft.startAt);
    else if (f !== "title") (next as unknown as Record<string, unknown>)[f] = fresh[f];
  }
  const set: TemplateField[] = [];
  const { games, presets } = ctx.options;
  let title: string | null = null;
  if (template) {
    const keys = templateKeysOf(template.templateJson);
    if (keys && offered(games, keys.game)) (next.game = keys.game), set.push("game");
    if (keys && offered(presets, keys.lobbyPreset)) (next.lobbyPreset = keys.lobbyPreset), set.push("lobbyPreset");
    if (keys && offered(presets, keys.preset)) (next.tablesPreset = keys.preset), set.push("tablesPreset");
    if (keys?.seasonId && ctx.seasons.some((s) => s.id === keys.seasonId)) (next.seasonId = keys.seasonId), set.push("seasonId");
    next.minutes = Math.max(1, Math.round(template.durationMs / MINUTE));
    set.push("minutes");
    const named = templateJsonOf(template.templateJson).title;
    if (typeof named === "string" && named.trim() !== "" && !titleTyped) (title = named), set.push("title");
  }
  if (title !== null) next.title = title;
  else if (!titleTyped) next.title = next.game ? defaultTitle(nameOf(games, next.game), ctx.placeName) : "";
  return { draft: next, set };
}

/** A template's JSON as an object; empty when it does not read as one. */
function templateJsonOf(templateJson: string): Record<string, unknown> {
  return templateObjectOf(templateJson) ?? {};
}

/** Keys of a template's JSON that belong to its own game: its game mode, its
 *  scoring and its player-count overrides. Left out when the event is for
 *  another game (they would name another game's mode and metrics). */
export const GAME_SPECIFIC: ReadonlySet<Listed> = new Set<Listed>([
  "gatheringGameModeId", "leaderboardScoringMetrics", "overrideMatchmakingPlayerCounts",
  "overrideMatchmakingMinPlayerCount", "overrideMatchmakingMaxPlayerCount",
  "overrideMatchmakingWaitTimeForPartialMatchMs", "overrideMatchmakingAbsoluteMaxWaitTimeMs",
]);

/** Whether the event is for another game than the one the template names (its
 *  JSON's `gatheringActivitySlug`), so the template's game settings do not
 *  carry over. A template naming no game is never another game's. */
export function otherGame(template: TemplateRow, game: string): boolean {
  const own = templateJsonOf(template.templateJson).gatheringActivitySlug;
  return typeof own === "string" && own !== "" && own !== game;
}

/** What a template's JSON adds to the input: every key the input takes with a
 *  value (not null), but its times, place, host and template bookkeeping
 *  ({@link NOT_FROM_TEMPLATE}),
 *  and, for an event of another game (`game`), its game settings
 *  ({@link GAME_SPECIFIC}). The values are the saved admin form's, sent as they
 *  are (the backend checks them, as for the admin console). */
export function templateFields(templateJson: string, game?: string): Partial<CreateEventInput> {
  const json = templateJsonOf(templateJson);
  const own = json.gatheringActivitySlug;
  const sameGame = game === undefined || typeof own !== "string" || own === "" || own === game;
  const fields: Record<string, unknown> = {};
  for (const key of CREATE_EVENT_FIELDS) {
    // A null or missing value is skipped: the default stands (a null for a
    // required field would be refused outright).
    if (json[key] === null || json[key] === undefined) continue;
    if (NOT_FROM_TEMPLATE.has(key) || (!sameGame && GAME_SPECIFIC.has(key))) continue;
    fields[key] = json[key];
  }
  return fields as Partial<CreateEventInput>;
}

/** What the input is built with besides the draft. */
export interface InputContext {
  placeId: string;
  /** The signed-in user's username ("" when they have none). */
  username: string;
  now: number;
  /** The template the draft starts from, when it does. */
  template: TemplateRow | null;
  /** Fields carried over from an event made again (Cancel's undo, unit 6c),
   *  where a template's would go; never nulls. */
  carried?: Partial<CreateEventInput>;
}

/** The host: the signed-in user, by username ({@link FALLBACK_HOST} when they have none). */
export function hostOf(username: string): string {
  return username.trim() || FALLBACK_HOST;
}

/** The `admin_createEvent` input: the admin console's defaults, then the
 *  template's JSON (when there is one; its game settings only for its own
 *  game) or the fields carried from an event made again, then the form. Fixed whatever the
 *  rest says: the place is this one, it is not a template, the signed-in user
 *  hosts, no Intercom segment is notified, it is revealed now (or at its
 *  start, if sooner) and winds down after its run. */
export function buildCreateEventInput(draft: ScheduleDraft, ctx: InputContext): CreateEventInput {
  const revealAt = Math.min(Math.floor(ctx.now / MINUTE) * MINUTE, draft.startAt);
  return {
    ...ADMIN_EVENT_DEFAULTS,
    ...(ctx.template ? templateFields(ctx.template.templateJson, draft.game) : {}),
    ...ctx.carried,
    // No Intercom segment, whatever the template says: a push to players is an
    // outside-facing side effect, never sent from the portal (the one place this is set).
    intercomNotificationSegment: "__NONE__",
    title: draft.title.trim(),
    gatheringActivitySlug: draft.game,
    lobbyGatheringSettingsTagSlug: draft.lobbyPreset,
    gatheringSettingsTagSlug: draft.tablesPreset,
    revealAt,
    startAt: draft.startAt,
    beginWindDownAt: draft.startAt + draft.minutes * MINUTE,
    seasonId: draft.seasonId,
    placeId: ctx.placeId,
    isTemplate: false,
    gatheringHostUsername: hostOf(ctx.username),
    ...(ctx.template ? { eventTemplateId: ctx.template.id } : {}),
  };
}

/** A field of the form (a draft's, but the template it starts from). */
export type DraftField = Exclude<keyof ScheduleDraft, "templateId">;

/** What stops the draft from being reviewed, in plain words, each with the
 *  field it is about; empty when nothing does. A season picked must be
 *  running at the start (`seasons`: the ones offered; a season not among them
 *  is not checked). Change (unit 6c) keeps only those about what it changes. */
export function fieldProblems(draft: ScheduleDraft, now: number, seasons: readonly SeasonOption[] = []): [DraftField, string][] {
  const season = seasons.find((s) => s.id === draft.seasonId);
  const problems: [DraftField, string | false | undefined | null][] = [
    ["game", !draft.game && "Pick a game."],
    [
      "startAt",
      Number.isNaN(draft.startAt)
        ? "Pick when it starts."
        : draft.startAt < now + SOONEST_START_MS && "Pick a start at least 2 minutes from now.",
    ],
    [
      "seasonId",
      season && !Number.isNaN(draft.startAt) && (draft.startAt < season.startAt || draft.startAt >= season.endAt) &&
        `${season.title} is not running then; pick its dates, another season, or none.`,
    ],
    ["minutes", !(draft.minutes >= 1) && "Let it run at least a minute."],
    // One line for the two presets, tied to each that is missing.
    ["lobbyPreset", !draft.lobbyPreset && "Pick the rules."],
    ["tablesPreset", !draft.tablesPreset && "Pick the rules."],
    ["title", !draft.title.trim() && "Give it a title."],
  ];
  return problems.filter((p): p is [DraftField, string] => !!p[1]);
}

/** Problems as said, each once (the two presets share one line). */
export function problemLines(problems: readonly [DraftField, string][]): string[] {
  return [...new Set(problems.map(([, problem]) => problem))];
}

/** What stops the draft from being reviewed, in plain words ({@link fieldProblems}). */
export function draftProblems(draft: ScheduleDraft, now: number, seasons: readonly SeasonOption[] = []): string[] {
  return problemLines(fieldProblems(draft, now, seasons));
}

/** What the review line names besides the input. */
export interface ReviewContext {
  options: ScheduleOptions;
  seasons: readonly SeasonOption[];
  username: string;
  template: TemplateRow | null;
  /** For tests; the reader's own otherwise. */
  timeZone?: string;
  locale?: string;
}

/** The rules as said: "Casual"; "House rules at the tables, Open table in the lobby" when the two differ. */
export function rulesLabel(input: Pick<CreateEventInput, "gatheringSettingsTagSlug" | "lobbyGatheringSettingsTagSlug">, presets: readonly PresetOption[]): string {
  const tables = nameOf(presets, input.gatheringSettingsTagSlug);
  const lobby = nameOf(presets, input.lobbyGatheringSettingsTagSlug);
  return tables === lobby ? tables : `${tables} at the tables, ${lobby} in the lobby`;
}

/** How many people a guest list names (`guestListByUsernames`: comma or space separated). */
export function guestCount(input: Pick<CreateEventInput, "guestListByUsernames">): number {
  return (input.guestListByUsernames ?? "").split(/[\s,]+/).filter((u) => u.trim() !== "").length;
}

/** What an event carries that changes who sees it, as the review lines name
 *  it: visibility when not public, a guest list, featured. */
export function whoSeesItParts(event: { visibility?: string | null; guests: number; featured?: boolean | null }): string[] {
  return [
    event.visibility && event.visibility !== "PUBLIC" && `visibility: ${event.visibility.toLowerCase()}`,
    event.guests > 0 && `guest list: ${event.guests} ${event.guests === 1 ? "person" : "people"}`,
    event.featured === true && "featured",
  ].filter((part): part is string => !!part);
}

/** The one line "Review" shows before anything is made: "Hearts at The Parlor
 *  · Tue, Oct 7, 7:00 – 8:00 PM PDT · rules: Casual · host: you · season: The
 *  Season". With no season it says it will not show under Next up; it names
 *  what else a template carries that changes who sees it (visibility when not
 *  public, a guest list, featured); from a template, which one. */
export function reviewLine(input: CreateEventInput, ctx: ReviewContext): string {
  const host = input.gatheringHostUsername === ctx.username.trim() ? "you" : input.gatheringHostUsername;
  const season = ctx.seasons.find((s) => s.id === input.seasonId);
  return [
    input.title,
    windowLabel(input.startAt, input.beginWindDownAt, { timeZone: ctx.timeZone, locale: ctx.locale }),
    `game: ${nameOf(ctx.options.games, input.gatheringActivitySlug)}`,
    `rules: ${rulesLabel(input, ctx.options.presets)}`,
    `host: ${host}`,
    season ? `season: ${season.title}` : "no season, so it will not show under Next up",
    ...whoSeesItParts({ visibility: input.visibility, guests: guestCount(input), featured: input.featured }),
    ctx.template && `from the template ${ctx.template.name}`,
    ctx.template && otherGame(ctx.template, input.gatheringActivitySlug) && "its game mode, scoring and player counts left out (another game)",
  ]
    .filter((part): part is string => !!part)
    .join(" · ");
}

/** How an undo went, as the backend did it: cancelled; ended (within a
 *  minute of its start); or, that close to the minute that the two clocks
 *  cannot tell, either. */
export type UndoOutcome = "cancelled" | "ended" | "either";

/** An event just made here, and where its undo stands. */
export interface MadeEvent {
  id: string;
  title: string;
  startAt: number;
  /** The season it joined; null: none (so it is not under Next up). */
  seasonId: string | null;
  /** When it was made (ms since epoch): "Undo" lasts five minutes from then (`UNDO_MS`). */
  madeAt: number;
  /** "cancelled" or "ended": undone, as the backend did it (it ends an event
   *  within a minute of its start instead of cancelling it). */
  undo: "ready" | "undoing" | UndoOutcome | { failed: string };
}

/** The create and its undo, and (unit 6c) the read and change of one event
 *  coming up (`schedule-event-api.ts`, with the viewer bound). */
export interface ScheduleWriteApi {
  create(input: CreateEventInput): Promise<MadeEventData>;
  cancel(eventId: string): Promise<void>;
  event(eventId: string): Promise<EventToChangeData | null>;
  update(input: UpdateEventInput): Promise<void>;
}

/** After a create whose answer was lost: the Next up row that is the event
 *  sent, if it was made — new since `before` (the row ids shown when Create
 *  was pressed), with the title and start sent. */
export function findMadeRow<Row extends { id: string; title: string; startAt: number }>(
  rows: readonly Row[],
  before: ReadonlySet<string>,
  input: Pick<CreateEventInput, "title" | "startAt">,
): Row | undefined {
  return rows.find((r) => !before.has(r.id) && r.title === input.title && r.startAt === input.startAt);
}

/** Make the event; the answer, stamped `now`, with its undo ready. */
export async function makeEvent(input: CreateEventInput, api: ScheduleWriteApi, now: number): Promise<MadeEvent> {
  const made = await api.create(input);
  return { id: made.id, title: made.title, startAt: made.startAt, seasonId: input.seasonId ?? null, madeAt: now, undo: "ready" };
}

/** Whether "Undo" still shows for `made` at `now`. */
export function canUndo(made: MadeEvent, now: number): boolean {
  return undoOffered(made, now);
}

/** Undo: cancel the event just made, by its id; says how the backend undid it
 *  (`cancelEvent` ends one within a minute of its start: "cancelled" only when
 *  even the answer's arrival was clear of that minute plus clock skew, "ended"
 *  only when even the sending was inside it, else "either"). Refused once the
 *  five minutes have passed (the admin console cancels it then). */
export async function undoEvent(
  made: MadeEvent,
  api: ScheduleWriteApi,
  now: number,
  clock: () => number = Date.now,
): Promise<UndoOutcome> {
  if (!undoWindowOpen(made, now)) throw new Error("The five minutes to undo have passed. Cancel it from the admin console.");
  await api.cancel(made.id);
  return cancelOutcomeOf(made.startAt, now, clock());
}

/** How `cancelEvent` went for an event starting at `startAt`, sent at `sent`
 *  and answered at `answered` (this browser's clock): "cancelled" only when
 *  even the answer's arrival was clear of the minute before its start plus
 *  clock skew, "ended" only when even the sending was inside it, else "either"
 *  (the backend decided somewhere between, by its own clock). */
export function cancelOutcomeOf(startAt: number, sent: number, answered: number): UndoOutcome {
  if (startAt - answered > END_INSTEAD_MS + CLOCK_SKEW_MS) return "cancelled";
  if (startAt - sent <= END_INSTEAD_MS - CLOCK_SKEW_MS) return "ended";
  return "either";
}

/** What shows where "Undo" was when an undo failed and it then could no
 *  longer go (its five minutes ran out, or the event started). */
export const UNDO_LAPSED_LINE = "Undo did not go through, and it can no longer go from here: use the admin console.";

/** Whether `made` has been undone (cancelled or ended). */
export function wasUndone(made: MadeEvent): boolean {
  return made.undo === "cancelled" || made.undo === "ended" || made.undo === "either";
}

/** What an undo done says, as the backend did it. */
export function undoneLine(made: MadeEvent): string {
  if (made.undo === "ended") return `Undone: ${made.title} was about to start, so pop.in ended it instead of cancelling it.`;
  if (made.undo === "either") {
    return `Undone: ${made.title} was about to start, so pop.in cancelled it or, if it had reached its last minute, ended it.`;
  }
  return `Undone: ${made.title} is cancelled.`;
}

/** A create whose answer never came back (pop.in unreachable, or the answer
 *  cut off), so the event may have been made. Create is not offered again in
 *  that form. */
export class LostAnswerError extends Error {
  /** `line`: what a lost change or cancel says instead (unit 6c). */
  constructor(rowsKnown: boolean, line?: string) {
    super(
      line ??
      (rowsKnown
        ? "Could not hear back from pop.in, so the event may have been made. Next up is read again: if it shows there, it gets its Undo. Check Next up or the admin console before scheduling it again."
        : "Could not hear back from pop.in, so the event may have been made. Check the admin console before scheduling it again."),
    );
    this.name = "LostAnswerError";
  }
}


/** Where the event just made stands against Next up, as read: shown there (its
 *  row carries "Undo"), or why it is not. */
export function madeNotShownLine(made: MadeEvent): string {
  if (!made.seasonId) return `${made.title} has no season here, so it does not show under Next up.`;
  return `${made.title} is not among the next events shown here.`;
}
